/**
 * Millisecond fields, human units. The wire and the row keep milliseconds
 * (`interval_ms`, `event_interval_ms` — the backend contract is unchanged);
 * the card shows a number with a seconds / minutes / hours unit beside it
 * (Mike, 2026-09-01: "instead of ms on the fe, allow for seconds, minutes
 * and hours to be set to make it easier to understand"). Pure.
 */

export type DurationUnit = 's' | 'min' | 'h';

export const DURATION_UNITS: ReadonlyArray<{ unit: DurationUnit; label: string; ms: number }> = [
  { unit: 's', label: 'seconds', ms: 1_000 },
  { unit: 'min', label: 'minutes', ms: 60_000 },
  { unit: 'h', label: 'hours', ms: 3_600_000 },
];

export const unitToMs = (unit: DurationUnit): number =>
  DURATION_UNITS.find((u) => u.unit === unit)?.ms ?? 1_000;

/**
 * The largest unit that divides the milliseconds cleanly, so 900000 reads
 * as "15 minutes", 3600000 as "1 hour", and 90000 as "90 seconds" — never a
 * fraction the operator has to decode. Null for a value that is not a
 * positive finite number.
 */
export const msToDuration = (ms: unknown): { value: number; unit: DurationUnit } | null => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  for (let i = DURATION_UNITS.length - 1; i >= 0; i--) {
    const { unit, ms: size } = DURATION_UNITS[i];
    if (n % size === 0) return { value: n / size, unit };
  }
  // sub-second precision: seconds with up to three decimals
  return { value: Math.round(n) / 1_000, unit: 's' };
};

/** The number the field displays for a stored ms in a given unit (≤3 decimals). */
export const displayValue = (ms: unknown, unit: DurationUnit): number | '' => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return Math.round((n / unitToMs(unit)) * 1_000) / 1_000;
};

/** The milliseconds a typed number means in a unit; null when unusable. */
export const durationToMs = (value: unknown, unit: DurationUnit): number | null => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * unitToMs(unit));
};
